<template>
  <div class="promotion-timer">
    <p class="promotion-timer__deadline" :class="{ end: timeOff }">
      {{ _T(textOff.text) }}
      <span>{{ normalizeDate(period.startAt) }} </span> до
      <span>{{ normalizeDate(period.endAt) }}</span>
    </p>
    <div class="promotion-timer__container">
      <template v-if="!timeOff">
        <div
          v-for="(item, index) in timer"
          :key="index"
          class="promotion-timer__items"
        >
          <div class="promotion-timer__item">
            <p class="promotion-timer__item-number">{{ item.number }}</p>
            <p class="promotion-timer__item-text">{{ _T(item.text) }}</p>
          </div>
          <div v-if="item.dots" class="promotion-timer__dots" />
        </div>
      </template>
      <p v-else class="promotion-timer__title">
        {{ _T(textOff.promotionOff) }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { countTimer } from "~/utils/timer";

const { period, timeOff } = defineProps({
  period: { type: Object, required: true },
  timeOff: { type: Boolean, required: false },
});

const textOff = {
  promotionOff: "@The promotion has ended",
  text: "@The promotion is valid from",
};

const timer = ref(null);

const seconds = ref(0);
const timerId = ref(null);

const isTime = () => {
  const item = countTimer({ timerId, period });
  seconds.value = item.timer.value[3].number;
  timer.value = item.timer.value;
};

watch(seconds, () => {
  timerId.value = setInterval(isTime, 1000);
});

onMounted(() => {
  isTime();
});
</script>

<style lang="scss" scoped>
.promotion-timer {
  &__deadline {
    margin-bottom: 23px;

    @include font(16, 20, 400);

    @include smallestScreen {
      margin-bottom: 16px;
    }

    & span {
      color: var(--color-primary-base);

      @include mobile {
        margin-top: 8px;
      }
    }

    &.end span {
      color: #8a8a8a;
    }
  }

  &__container {
    @include flex-container(row, flex-start, center);
    gap: 16px;

    @include smallestScreen {
      gap: 12px;
    }

    @include bigMobile {
      padding: 0 30px;
    }

    @include mobile {
      max-width: 308px;
      width: 308px;
    }

    @include mobile {
      gap: 4px;
      justify-content: center;
    }
  }

  &__items {
    @include flex-container(row, center, center);
    gap: 16px;

    @include smallestScreen {
      gap: 12px;
    }

    @include mobile {
      gap: 8px;
    }
  }

  &__item {
    position: relative;

    width: 60px;
    height: 70px;
    color: #3d3b38;

    @include mobile {
      height: 50px;
    }
  }

  &__item-number {
    @include font(32, 36, 700);
    text-align: center;

    @include mobile {
      @include font(16, 23, 700);
    }
  }

  &__item-text {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translate(-50%);

    @include font(18, 18, 500);
    text-transform: lowercase;

    @include mobile {
      @include font(16, 23, 500);
      letter-spacing: 0.32px;
    }
  }

  &__dots {
    position: relative;
    width: 6px;
    height: 20px;
    margin-top: 8px;

    animation: visible 2s infinite linear;

    align-self: start;

    @include mobile {
      width: 3px;
      height: 10px;
    }

    &::after {
      content: "";
      position: absolute;
      z-index: 2;
      top: 0;
      left: 0;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: var(--color-primary-base);

      @include mobile {
        width: 3px;
        height: 3px;
      }
    }

    &::before {
      content: "";
      position: absolute;
      z-index: 2;
      bottom: 0;
      left: 0;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: var(--color-primary-base);

      @include mobile {
        width: 3px;
        height: 3px;
      }
    }
  }

  &__title {
    @include font(25, 35, 700);
    letter-spacing: 0.5px;
    white-space: nowrap;
    text-transform: uppercase;
    color: var(--color-sky-base);

    @include smallestScreen {
      @include font(20, 30, 700);
    }

    @include mobile {
      text-transform: none;
      @include font(16, 23, 700);
      letter-spacing: 0.32px;
    }
  }
}

@keyframes visible {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}
</style>
